import { formatLine, normKey, parseLine, type Id, type ParsedLine } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { commit, newId, type Writer } from '../db/commit';
import { db } from '../db/db';
import type { ListItemRow, ListRow } from '../db/types';
import { currentUserId } from '../lib/auth';
import { resolveProduct, type DraftItem, type Resolution } from './products';
import { repointCheck } from './shopping';

export function useLists(status?: ListRow['status']): ListRow[] | undefined {
  return useLiveQuery(async () => {
    const all = await db.lists.toArray();
    return all
      .filter((l) => !l.deleted && (status === undefined || l.status === status))
      .sort((a, b) => (b.createdMs ?? 0) - (a.createdMs ?? 0));
  }, [status]);
}

export function useList(id: Id | undefined): ListRow | null | undefined {
  return useLiveQuery(async () => (id ? ((await db.lists.get(id)) ?? null) : null), [id]);
}

export function useListItems(listId: Id | undefined): ListItemRow[] | undefined {
  return useLiveQuery(
    async () =>
      listId
        ? (await db.listItems.where('listId').equals(listId).toArray())
            .filter((i) => !i.deleted)
            .sort((a, b) => a.position - b.position)
        : [],
    [listId],
  );
}

export function defaultListTitle(now = new Date()): string {
  return `Zakupy ${now.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' })}`;
}

const shortDate = (d: Date) =>
  `${d.toLocaleDateString('pl-PL', { weekday: 'short', day: 'numeric', month: 'numeric' })}, ${d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`;

export interface NewListInput {
  title?: string;
  storeId?: Id | null;
  items: DraftItem[];
  /** Decyzje dla pozycji z kandydatami (indeks pozycji → decyzja). Brak decyzji = nowy produkt. */
  resolutions?: Record<number, Resolution>;
  /** Konkretne towary z paragonów przypięte do pozycji (indeks pozycji → artykuł). */
  pins?: Record<number, Id>;
}

/** Tworzy listę z rozpoznanymi produktami w jednej transakcji. */
export async function createList(input: NewListInput): Promise<Id> {
  const listId = newId();
  const now = Date.now();
  // Domyślny tytuł z nazwą sklepu, żeby kilka list z jednego dnia dało się odróżnić.
  const storeName = input.storeId ? (await db.stores.get(input.storeId))?.name : null;
  const fallback = storeName ? `${storeName} · ${shortDate(new Date(now))}` : defaultListTitle(new Date(now));
  await commit(async (w) => {
    await w.set('lists', listId, {
      title: input.title?.trim() || fallback,
      storeId: input.storeId ?? null,
      status: 'active',
      createdMs: now,
      finishedMs: null,
    });
    const created = new Map<string, Id>();
    for (const [i, item] of input.items.entries()) {
      const productId = await resolveProduct(w, item, input.resolutions?.[i], created);
      const articleId = await pinArticle(w, input.pins?.[i], productId);
      await w.set('listItems', newId(), {
        listId,
        ...lineFields(item.line),
        productId,
        articleId,
        position: i,
        status: 'pending',
        partialQty: null,
        substitute: null,
        checkedMs: null,
        addedBy: currentUserId(),
        addedMs: Date.now(),
      });
    }
  });
  return listId;
}

/** Dopisuje pozycje do istniejącej listy (np. braki przeniesione z poprzednich zakupów). */
export async function addItems(listId: Id, items: DraftItem[], resolutions?: Record<number, Resolution>, pins?: Record<number, Id>): Promise<void> {
  const existing = await db.listItems.where('listId').equals(listId).toArray();
  let position = existing.reduce((m, i) => Math.max(m, i.position), -1) + 1;
  await commit(async (w) => {
    const created = new Map<string, Id>();
    for (const [i, item] of items.entries()) {
      const productId = await resolveProduct(w, item, resolutions?.[i], created);
      const articleId = await pinArticle(w, pins?.[i], productId);
      await w.set('listItems', newId(), {
        listId,
        ...lineFields(item.line),
        productId,
        articleId,
        position: position++,
        status: 'pending',
        partialQty: null,
        substitute: null,
        checkedMs: null,
        addedBy: currentUserId(),
        addedMs: Date.now(),
      });
    }
  });
}

/** Przypięty artykuł bez produktu uczy się produktu pozycji (następnym razem cena pokaże się sama). */
async function pinArticle(w: Writer, articleId: Id | undefined, productId: Id | null): Promise<Id | null> {
  if (!articleId) return null;
  const a = await db.articles.get(articleId);
  if (!a || a.deleted) return null;
  if (!a.productId && productId) await w.set('articles', articleId, { productId, linkSource: 'pin' });
  return articleId;
}

type LineFields = Pick<ListItemRow, 'rawText' | 'name' | 'qty' | 'unit' | 'amountValue' | 'amountUnit' | 'note'>;

// Uwagi z listy („najlepiej bio”, „dłuższa data”) trafiają do notatki pozycji.
const lineFields = (line: DraftItem['line']): LineFields => ({
  rawText: line.raw,
  name: line.name,
  qty: line.qty,
  unit: line.unit,
  amountValue: line.amount?.value ?? null,
  amountUnit: line.amount?.unit ?? null,
  note: line.note,
});

/**
 * Pozycja jako linia listy: tak, jak ją napisano, jeśli nadal się zgadza (ilość i uwaga mogły się zmienić),
 * inaczej złożona z pól. parseLine odczyta z niej te same pola.
 */
export function itemLine(item: ListItemRow): string {
  const line: Omit<ParsedLine, 'raw'> = {
    name: item.name,
    qty: item.qty ?? null,
    unit: (item.unit ?? null) as ParsedLine['unit'],
    amount: item.amountValue != null ? { value: item.amountValue, unit: item.amountUnit as Amount['unit'] } : null,
    note: item.note ?? null,
  };
  const raw = item.rawText ? parseLine(item.rawText) : null;
  const same =
    raw !== null &&
    raw.name === line.name &&
    raw.qty === line.qty &&
    raw.unit === line.unit &&
    raw.amount?.value === line.amount?.value &&
    raw.amount?.unit === line.amount?.unit &&
    raw.note === line.note;
  return same ? item.rawText! : formatLine(line);
}

type Amount = NonNullable<ParsedLine['amount']>;

/**
 * Zmienia nazwę, ilość i uwagę pozycji (linia jak w SMS-ie). Inna nazwa to inny produkt,
 * dopasowany jak przy dopisywaniu; ta sama nazwa zostawia produkt (także wybrany wcześniej jako „to samo”).
 */
export async function editItem(itemId: Id, item: DraftItem, resolution?: Resolution): Promise<void> {
  await commit(async (w) => {
    const current = await db.listItems.get(itemId);
    if (!current || current.deleted) throw new Error('Nie ma takiej pozycji');
    const keep = current.productId !== null && normKey(current.name) === item.key;
    const productId = keep ? current.productId : await resolveProduct(w, item, resolution);
    const next: Partial<ListItemRow> = { ...lineFields(item.line), productId };
    // Tylko zmienione pola — reszta nie nadpisuje równoległych zmian domowników (LWW per pole).
    const fields = Object.fromEntries(
      Object.entries(next).filter(([k, v]) => (current[k as keyof ListItemRow] ?? null) !== v),
    ) as Partial<ListItemRow>;
    if (Object.keys(fields).length === 0) return;
    await w.set('listItems', itemId, fields);
    if (productId && productId !== current.productId) await repointCheck(w, itemId, productId);
  });
}

/** Usuwa pozycję z listy (wiersz zostaje oznaczony jako usunięty, więc da się go przywrócić). */
export async function removeItem(itemId: Id): Promise<void> {
  await commit((w) => w.set('listItems', itemId, { deleted: true }));
}

export async function restoreItem(itemId: Id): Promise<void> {
  await commit((w) => w.set('listItems', itemId, { deleted: false }));
}

/** Stan nawigacji do /new: tekst listy (udostępniony albo z innej listy) i sklep do podstawienia. */
export interface NewListState {
  text: string;
  storeId?: Id | null;
  /** Tytuł listy, z której pochodzą pozycje. */
  from?: string | null;
}

/** Stan dla „Nowa lista na podstawie tej”: pozycje do poprawienia przed zapisaniem, ten sam sklep. */
export async function newListFrom(list: ListRow): Promise<NewListState> {
  return { text: await listText(list.id), storeId: list.storeId, from: list.title };
}

/** Pozycje listy jako tekst do nowej listy — linia po linii, w kolejności z listy. */
export async function listText(listId: Id): Promise<string> {
  const items = (await db.listItems.where('listId').equals(listId).toArray()).filter((i) => !i.deleted);
  return items
    .sort((a, b) => a.position - b.position)
    .map(itemLine)
    .join('\n');
}

export async function setListStore(listId: Id, storeId: Id | null): Promise<void> {
  await commit(async (w) => {
    const list = await db.lists.get(listId);
    const fields: Partial<ListRow> = { storeId };
    // Domyślny tytuł („Lidl · niedz., 11.10”) idzie za sklepem; tytułu nadanego ręcznie nie ruszamy.
    const created = list?.createdMs;
    if (list && created != null) {
      const nameOf = async (id: Id | null | undefined) => (id ? (await db.stores.get(id))?.name : undefined);
      const titleFor = (name: string | null | undefined) =>
        name ? `${name} · ${shortDate(new Date(created))}` : defaultListTitle(new Date(created));
      if (list.title === titleFor(await nameOf(list.storeId))) fields.title = titleFor(await nameOf(storeId));
    }
    await w.set('lists', listId, fields);
  });
}

export async function renameList(listId: Id, title: string): Promise<void> {
  // Pusta nazwa = bez zmian (nie nadpisujemy tytułem z dzisiejszą datą).
  if (!title.trim()) return;
  await commit((w) => w.set('lists', listId, { title: title.trim() }));
}

export async function archiveList(listId: Id): Promise<void> {
  await commit((w) => w.set('lists', listId, { status: 'archived' }));
}

/** Przywraca listę z archiwum do zakończonych. */
export async function unarchiveList(listId: Id): Promise<void> {
  await commit((w) => w.set('lists', listId, { status: 'done' }));
}

export async function deleteList(listId: Id): Promise<void> {
  await commit((w) => w.set('lists', listId, { deleted: true }));
}

/** Krótki opis ilości do wyświetlenia przy pozycji, np. „2×”, „1,5 kg”, „2× 1 l”. */
export function quantityLabel(i: Pick<ListItemRow, 'qty' | 'unit' | 'amountValue' | 'amountUnit'>): string {
  const parts: string[] = [];
  if (i.qty !== null && i.qty !== undefined) parts.push(i.unit === 'opak' ? `${fmt(i.qty)} opak.` : `${fmt(i.qty)}×`);
  if (i.amountValue !== null && i.amountValue !== undefined) parts.push(`${fmt(i.amountValue)} ${i.amountUnit ?? ''}`.trim());
  return parts.join(' ');
}

const fmt = (n: number) => n.toLocaleString('pl-PL', { maximumFractionDigits: 2 });
